import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Package, Save, X } from 'lucide-react';
import { vendorApi, productCategoryApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';

export default function VendorProductForm() {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const vendorId = Number(searchParams.get('vendorId'));
  const isEdit = Boolean(productId);

  const [formData, setFormData] = useState({
    name: '',
    variety: '',
    description: '',
    category: '',
    image: '',
    emoji: '',
    is_organic: false,
  });

  const [categories, setCategories] = useState<{ id: number; name: string; emoji: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await productCategoryApi.getCategories();
        setCategories(data);
        // Set default category if creating new product
        if (!isEdit && data.length > 0) {
          setFormData(prev => ({ ...prev, category: data[0].name }));
        }
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load categories:', apiError);
        setError(apiError.message);
      }
    };
    fetchCategories();
  }, [isEdit]);

  // Load existing product if editing
  useEffect(() => {
    if (isEdit && productId && vendorId) {
      const fetchProduct = async () => {
        try {
          setLoadingProduct(true);
          const product = await vendorApi.getVendorProduct(vendorId, Number(productId));
          setFormData({
            name: product.name || '',
            variety: product.variety || '',
            description: product.description || '',
            category: product.category || '',
            image: product.image || '',
            emoji: product.emoji || '',
            is_organic: product.is_organic || false,
          });
        } catch (err: unknown) {
          const apiError = extractApiError(err);
          console.error('Failed to load product:', apiError);
          setError(apiError.message);
        } finally {
          setLoadingProduct(false);
        }
      };
      fetchProduct();
    }
  }, [isEdit, productId, vendorId]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.length > 200) {
      errors.name = 'Product name must be 200 characters or less';
    }

    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (formData.description && formData.description.length > 2000) {
      errors.description = 'Description must be 2000 characters or less';
    }

    if (formData.variety && formData.variety.length > 100) {
      errors.variety = 'Variety must be 100 characters or less';
    }

    if (formData.image && !isValidUrl(formData.image)) {
      errors.image = 'Image must be a valid URL';
    }

    if (formData.emoji && formData.emoji.length > 10) {
      errors.emoji = 'Emoji field is too long';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!vendorId) {
      setError('Vendor ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Clean up data (remove empty strings, convert to null/undefined)
      const productData = {
        name: formData.name.trim(),
        variety: formData.variety.trim() || undefined,
        description: formData.description.trim() || undefined,
        category: formData.category,
        image: formData.image.trim() || undefined,
        emoji: formData.emoji.trim() || undefined,
        is_organic: formData.is_organic,
      };

      if (isEdit && productId) {
        await vendorApi.updateVendorProduct(vendorId, Number(productId), productData);
      } else {
        await vendorApi.createVendorProduct(vendorId, productData);
      }

      // Navigate back to product list
      navigate('/vendor/products');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to save product:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendor/products');
  };

  if (loadingProduct) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6" />
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update product information' : 'Add a new product to your catalog'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Handmade Necklace"
              maxLength={200}
            />
            {validationErrors.name && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.name}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              {formData.name.length}/200 characters
            </p>
          </div>

          {/* Variety */}
          <div>
            <label htmlFor="variety" className="block text-sm font-medium text-gray-700 mb-1">
              Variety (Optional)
            </label>
            <input
              type="text"
              id="variety"
              value={formData.variety}
              onChange={(e) => setFormData({ ...formData, variety: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.variety ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Sterling Silver, Beaded, Chain"
              maxLength={100}
            />
            {validationErrors.variety && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.variety}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              Specific style, material, or variation
            </p>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.category ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>
                  {cat.emoji} {cat.name}
                </option>
              ))}
            </select>
            {validationErrors.category && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.category}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Describe your product..."
              maxLength={2000}
            />
            {validationErrors.description && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.description}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              {formData.description.length}/2000 characters
            </p>
          </div>

          {/* Image URL */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
              Image URL (Optional)
            </label>
            <input
              type="url"
              id="image"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.image ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="https://example.com/image.jpg"
            />
            {validationErrors.image && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.image}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              URL to product image
            </p>
          </div>

          {/* Emoji */}
          <div>
            <label htmlFor="emoji" className="block text-sm font-medium text-gray-700 mb-1">
              Emoji Icon (Optional)
            </label>
            <input
              type="text"
              id="emoji"
              value={formData.emoji}
              onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                validationErrors.emoji ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 📿 or 💍"
              maxLength={10}
            />
            {validationErrors.emoji && (
              <p className="text-red-600 text-sm mt-1">{validationErrors.emoji}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              Emoji to display with product (optional)
            </p>
          </div>

          {/* Organic Checkbox */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="is_organic"
                checked={formData.is_organic}
                onChange={(e) => setFormData({ ...formData, is_organic: e.target.checked })}
                className="w-4 h-4 text-secondary border-gray-300 rounded focus:ring-primary"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="is_organic" className="text-sm font-medium text-gray-700">
                Organic/Eco-friendly
              </label>
              <p className="text-gray-500 text-sm">
                Check if this product is organic, sustainable, or eco-friendly
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEdit ? 'Update Product' : 'Create Product'}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
